// src/components/BillingHistory.jsx
import React, { Component } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

class BillingHistory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      balance: 0,
      subscriptions: [],
      invoices: [],
      creditPurchases: [],
      creditUsage: [],
      error: null,
      portalBusy: false,
      sendReceiptBusyId: null
    };

    this.API_BASE = process.env.REACT_APP_API_ENDPOINT;
  }

  componentDidMount() {
    this.loadAll();
  }

  /* ==============================
     LOAD ALL BILLING DATA
  ============================== */
async loadAll() {
  try {
    const [
      balanceRes,
      subsRes,
      creditPayRes,
      invoicesRes,
      usageRes,
    ] = await Promise.allSettled([
      axios.get(`${this.API_BASE}/stripe/credits/balance`, { withCredentials: true }),
      axios.get(`${this.API_BASE}/stripe/subscriptions/history`, { withCredentials: true }),
      axios.get(`${this.API_BASE}/stripe/payments/credits/history`, { withCredentials: true }),
      axios.get(`${this.API_BASE}/stripe/invoices`, { withCredentials: true }),
      axios.get(`${this.API_BASE}/stripe/credits/history?limit=50`, { withCredentials: true }),
    ]);

    // --- Balance ---
    const balance =
      balanceRes.status === 'fulfilled'
        ? balanceRes.value?.data?.credits ?? 0
        : 0;

    // --- Subscriptions + invoices (from /subscriptions/history) ---
    let subscriptions = [];
    let invoicesFromSubs = [];
    let subscriptionMessage = null;

    if (subsRes.status === 'fulfilled') {
      const data = subsRes.value.data || {};
      subscriptions = data.subscriptions || [];
      invoicesFromSubs = data.invoices || [];
      subscriptionMessage = data.message || null;
    }

    // --- Invoices fallback from /stripe/invoices ---
    let invoices = invoicesFromSubs;
    if (!invoices.length && invoicesRes.status === 'fulfilled') {
      invoices = invoicesRes.value.data?.invoices || [];
    }

    // --- Credit purchases ---
    const creditPurchases =
      creditPayRes.status === 'fulfilled'
        ? creditPayRes.value.data?.creditPurchases || []
        : [];

    // --- Credit usage ---
    const creditUsage =
      usageRes.status === 'fulfilled'
        ? usageRes.value.data?.usage || []
        : [];

    this.setState({
      balance,
      subscriptions,
      invoices,
      creditPurchases,
      creditUsage,
      subscriptionMessage,
      loading: false,
      error: null,
    });
  } catch (err) {
    console.error('Error loading billing data:', err);
    this.setState({
      error: 'Unable to load billing data',
      loading: false,
    });
  }
}



  /* ==============================
     OPEN STRIPE BILLING PORTAL
  ============================== */
  handleOpenPortal = async () => {
    try {
      this.setState({ portalBusy: true });

      const res = await axios.post(
        `${this.API_BASE}/stripe/portal/session`,
        {},
        { withCredentials: true }
      );

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Unable to open billing portal.");
      }
    } catch (err) {
      console.error("Portal error", err);
      toast.error("Unable to open billing portal.");
    } finally {
      this.setState({ portalBusy: false });
    }
  };

  /* ==============================
     DATE FORMATTER
  ============================== */
  formatDate(ts) {
    if (!ts) return "-";
    const d = new Date(ts * 1000 || ts);
    return d.toLocaleString();
  }

  /* ==============================
     CREDIT BALANCE SECTION
  ============================== */
  renderCreditBalance() {
    return (
      <section className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900">Credits</h3>
        </div>

        <div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-md text-slate-500">Current balance:</span>
            <span className="text-2xl font-bold text-slate-900">
              {this.state.balance}
            </span>
          </div>

          <p className="text-md text-slate-500">
            Credits are consumed when you use AI / voice features in Caredevo.
          </p>
        </div>
      </section>
    );
  }

  /* ==============================
     SUBSCRIPTIONS SECTION
  ============================== */
  renderSubscriptions() {
    const { subscriptions, invoices, portalBusy } = this.state;

    return (
      <section className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900">Subscriptions</h3>

          <button
            className="inline-flex items-center px-3 py-1.5 rounded-full border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={portalBusy}
            onClick={this.handleOpenPortal}
          >
            {portalBusy ? "Opening portal…" : "Manage in Stripe portal"}
          </button>
        </div>

        {subscriptions.length === 0 && (
          <p className="text-sm text-slate-500">No subscription records found.</p>
        )}

        <div className="space-y-4">
          {subscriptions.map((s) => {
            // Match most recent invoice for this subscription
            const matchedInvoice = invoices.find(
              (inv) => inv.subscription === s.id
            );

            const invoiceId = matchedInvoice?.id || "—";

            const item = s.items?.data?.[0];
            const price = item?.price;

            const amount = price
              ? `${(price.unit_amount / 100).toFixed(2)} ${price.currency.toUpperCase()}`
              : "-";

            return (
              <div
                key={s.id}
                className="border border-slate-200 rounded-lg p-4 bg-white shadow-sm"
              >
                {/* INVOICE ID */}
                <div className="text-md font-semibold text-slate-900 mb-2">
                  Invoice: {invoiceId}
                </div>

                {/* DETAILS */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 text-sm text-slate-700">

                  <div>
                    <span className="font-medium">Start date: </span>
                    {this.formatDate(s.billing_cycle_anchor)}
                  </div>

                  <div>
                    <span className="font-medium">Amount: </span>
                    {amount} / {price?.recurring?.interval || "period"}
                  </div>

                  <div>
                    <span className="font-medium">Next billing: </span>
                    {this.formatDate(s.current_period_end)}
                  </div>

                  <div>
                    <span className="font-medium">End date: </span>
                    {s.cancel_at
                      ? this.formatDate(s.cancel_at)
                      : "Active (no end date)"}
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

 /* ==============================
   CREDIT PURCHASES (Top-ups)
  ============================== */
  renderCreditPurchases() {
    const { creditPurchases, invoices } = this.state;

    // Build lookup maps for invoices
    const invoiceById = {};
    const invoiceByPaymentIntent = {};

    invoices.forEach((inv) => {
      if (!inv) return;
      invoiceById[inv.id] = inv;
      if (inv.payment_intent) {
        invoiceByPaymentIntent[inv.payment_intent] = inv;
      }
    });

    return (
      <section className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900">Credit purchases</h3>
        </div>

        <div className="overflow-x-auto h-[300px]">
          {creditPurchases.length === 0 ? (
            <p className="text-md text-slate-500">No credit top-ups yet.</p>
          ) : (
            <table className="min-w-full text-md border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left">
                  <th className="py-2 px-2">Invoice</th>
                  <th className="py-2 px-2">Date</th>
                  <th className="py-2 px-2">Amount</th>
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2">Credits</th>
                  <th className="py-2 px-2">Invoice/Receipt</th>
       
                </tr>
              </thead>
              <tbody>
                {creditPurchases.map((s) => {
                  const matchedInvoice =
                    (s.invoice && invoiceById[s.invoice]) ||
                    (s.payment_intent && invoiceByPaymentIntent[s.payment_intent]);

                  return (
                    <tr key={s.id} className="border-b border-slate-100">
                      {/* INVOICE */}
                      <td className="py-2 px-2 break-all font-medium text-slate-900">
                        {matchedInvoice
                          ? matchedInvoice.number || matchedInvoice.id
                          : "-"}
                      </td>

                      {/* DATE */}
                      <td className="py-2 px-2">{this.formatDate(s.created)}</td>

                      {/* AMOUNT */}
                      <td className="py-2 px-2">
                        {s.amount_total
                          ? `${(s.amount_total / 100).toFixed(2)} ${s.currency?.toUpperCase()}`
                          : "-"}
                      </td>

                      {/* STATUS */}
                      <td className="py-2 px-2 capitalize">{s.payment_status}</td>

                      {/* CREDITS */}
                      <td className="py-2 px-2">
                        {s.metadata?.creditsAmount || "-"}
                      </td>

                      {/* VIEW INVOICE LINK */}
                      <td className="py-2 px-2">
                        {matchedInvoice?.hosted_invoice_url ? (
                          <a
                            href={matchedInvoice.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            View
                          </a>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>

                    
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    );
  }


  /* ==============================
    SUBSCRIPTION HISTORY SECTION
  ============================== */
  renderSubscriptionHistory() {
    const { invoices } = this.state;

    // Only show invoices that belong to subscriptions
    const subscriptionInvoices = (invoices || []).filter(
      (inv) => !!inv.subscription
    );

    return (
      <section className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900">
            Subscription history
          </h3>
        </div>

        <div className="overflow-x-auto h-[200px]">
          {subscriptionInvoices.length === 0 && (
            <p className="text-sm text-slate-500">
              No subscription invoices yet.
            </p>
          )}

          {subscriptionInvoices.length > 0 && (
            <table className="min-w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="py-2 px-2 font-semibold">Invoice</th>
                  <th className="py-2 px-2 font-semibold">Date</th>
                  <th className="py-2 px-2 font-semibold">Amount</th>
                  <th className="py-2 px-2 font-semibold">Status</th>
                  <th className="py-2 px-2 font-semibold">Invoice/Receipt</th>
          
                </tr>
              </thead>
              <tbody>
                {subscriptionInvoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100">
                    {/* Invoice number / id */}
                    <td className="py-2 px-2 break-all">
                      {inv.number || inv.id}
                    </td>

                    {/* Created date */}
                    <td className="py-2 px-2">
                      {this.formatDate(inv.created)}
                    </td>

                    {/* Amount */}
                    <td className="py-2 px-2">
                      {(inv.total / 100).toFixed(2)}{" "}
                      {inv.currency?.toUpperCase()}
                    </td>

                    {/* Status */}
                    <td className="py-2 px-2 capitalize">
                      {inv.status}
                    </td>

                    {/* View link */}
                    <td className="py-2 px-2">
                      {inv.hosted_invoice_url ? (
                        <a
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>

              
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    );
  }





  /* ==============================
     CREDIT USAGE SECTION
  ============================== */
  renderCreditUsage() {
    const { creditUsage } = this.state;

    return (
      <section className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-4">
        <h3 className="text-lg font-semibold text-slate-900 mb-3">
          Credit usage
        </h3>

        <div className="overflow-x-auto h-[500px]">
          {creditUsage.length === 0 ? (
            <p className="text-md text-slate-500">No usage logged yet.</p>
          ) : (
            <table className="min-w-full text-md border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="py-2 px-2">Date</th>
                  <th className="py-2 px-2">Amount</th>
                  <th className="py-2 px-2">Type</th>
                  <th className="py-2 px-2">Reason</th>
                </tr>
              </thead>

              <tbody>
                {creditUsage.map((u) => (
                  <tr key={u._id} className="border-b border-slate-100">
                    <td className="py-2 px-2">
                      {new Date(u.createdAt).toLocaleString()}
                    </td>
                    <td className="py-2 px-2">{u.amount}</td>
                    <td className="py-2 px-2">{u.type}</td>
                    <td className="py-2 px-2">
                      {u.reason || <span className="text-sm text-slate-400">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    );
  }

  /* ==============================
     SEND RECEIPT VIA EMAIL
  ============================== */
  handleSendReceipt = async (chargeId) => {
    if (!chargeId) {
      toast.error("No charge id found for this payment.");
      return;
    }

    try {
      this.setState({ sendReceiptBusyId: chargeId });

      await axios.post(
        `${this.API_BASE}/stripe/receipts/${chargeId}/send`,
        {},
        { withCredentials: true }
      );

      toast.success("Receipt emailed to customer.");
    } catch (err) {
      console.error("Send receipt error", err);
      const msg =
        err.response?.data?.error ||
        err.message ||
        "Unable to send receipt.";
      toast.error(msg);
    } finally {
      this.setState({ sendReceiptBusyId: null });
    }
  };





  /* ==============================
     MAIN RENDER
  ============================== */
  render() {
    const { loading, error } = this.state;

    if (loading) {
      return <div className="p-6 text-md text-slate-600">Loading billing data…</div>;
    }

    if (error) {
      return (
        <div className="p-4 rounded-md bg-red-50 text-md text-red-700">
          {error}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <h2 className="text-3xl  text-center font-bold text-slate-900 mb-2">
          Billing & member history
        </h2>

        {this.renderCreditBalance()}
        {this.renderSubscriptions()}
        {this.renderSubscriptionHistory()}
        {this.renderCreditPurchases()}
        {this.renderCreditUsage()}
      </div>
    );
  }
}

export default BillingHistory;
